import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Shield, Zap } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, settings } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || null;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Connexion réussie");
      if (res.user?.is_pending) {
        navigate("/pending-approval");
      } else if (from) {
        navigate(from);
      } else {
        navigate("/dashboard");
      }
    } else {
      setError(res.error);
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-[#0B0F17]">
      {/* Visual side */}
      <div className="hidden lg:block lg:col-span-3 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1583214499157-ce8d6f368fef?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Stadium"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B0F17]/90 via-[#0B0F17]/50 to-[#0B0F17]" />
        <div className="relative h-full flex flex-col justify-between p-16">
          <div className="flex items-center gap-3">
            {settings.app_logo_url ? (
              <img
                src={settings.app_logo_url}
                alt="Logo"
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-[#E5B85C] flex items-center justify-center">
                <Shield className="w-7 h-7 text-[#0B0F17]" strokeWidth={2.5} />
              </div>
            )}
            <div className="font-display font-black text-2xl uppercase tracking-tight">
              {settings.app_name || "ASS les Régionnaires"}
            </div>
          </div>
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E5B85C]/10 border border-[#E5B85C]/30 mb-6">
              <Zap className="w-3.5 h-3.5 text-[#E5B85C]" />
              <span className="text-[11px] font-mono-stat uppercase tracking-widest text-[#E5B85C]">
                Tactical System
              </span>
            </div>
            <h1 className="font-display font-black text-6xl uppercase tracking-tight leading-[0.9] mb-4">
              Le match
              <br />
              <span className="text-[#E5B85C]">commence ici.</span>
            </h1>
            <p className="text-slate-300 max-w-md text-base leading-relaxed">
              Gérez vos disponibilités, feuilles de match, buteurs et cartons — un centre de commande pour votre club.
            </p>
          </div>
          <div className="text-xs font-mono-stat uppercase tracking-widest text-slate-500">
            © {settings.app_name || "ASS les Régionnaires"}
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="lg:col-span-2 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md slide-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            {settings.app_logo_url ? (
              <img
                src={settings.app_logo_url}
                alt="Logo"
                className="w-11 h-11 rounded-full object-cover"
              />
            ) : (
              <div className="w-11 h-11 rounded-full bg-[#E5B85C] flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#0B0F17]" strokeWidth={2.5} />
              </div>
            )}
            <div className="font-display font-black text-2xl uppercase">
              {settings.app_name || "ASS les Régionnaires"}
            </div>
          </div>
          <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-slate-500 mb-3">
            Bienvenue
          </div>
          <h2 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight mb-3">
            Connexion
          </h2>
          <p className="text-slate-400 mb-8 text-sm">
            Accédez à votre feuille de match, statistiques et disponibilités.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
                Email
              </label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-slate-900 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none focus:ring-2 focus:ring-[#E5B85C]/20 text-white placeholder-slate-600"
                placeholder="votre.email@gmail.com"
              />
            </div>
            <div>
              <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
                Mot de passe
              </label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-slate-900 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none focus:ring-2 focus:ring-[#E5B85C]/20 text-white placeholder-slate-600"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div
                data-testid="login-error"
                className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm"
              >
                {error}
              </div>
            )}
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold uppercase tracking-wide rounded-lg disabled:opacity-50 transition-colors"
            >
              {loading ? "Connexion..." : "Se connecter"}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-400">
            <Link
              data-testid="forgot-password-link"
              to="/forgot-password"
              className="text-slate-300 hover:text-[#E5B85C] font-semibold"
            >
              Mot de passe oublié ?
            </Link>
          </div>
          <div className="mt-3 text-center text-sm text-slate-400">
            Pas de compte ?{" "}
            <Link
              data-testid="go-register-link"
              to="/register"
              className="text-[#E5B85C] hover:text-[#D4A24E] font-semibold"
            >
              Créer un compte
            </Link>
          </div>
          <div className="mt-3 text-center">
            <Link
              data-testid="go-install-link"
              to="/install"
              className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#E5B85C] uppercase tracking-widest font-mono-stat"
            >
              📱 Installer l'app sur mobile
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
